import { useQueryClient } from '@tanstack/react-query';
import { Camera, ChevronLeft, ChevronRight, ClipboardPaste, PencilLine, Sparkles } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { APP_NAME } from '../../../shared/brand';
import { IMPORT_LIMITS, type RecipeImportRequest } from '../../../shared/recipeImport';
import { ApiError } from '../../api/client';
import { kitchenUnitsNow, refreshAIUsage, useImportRecipe } from '../../api/hooks';
import { useAIAllowance } from '../../lib/aiAllowance';
import { AI_OFF_FIX, useAIOffReason } from '../../lib/aiOff';
import { prepareImage, type PreparedImage } from '../../lib/image';
import { AIRequestsLeft } from '../AIRequestsLeft';
import { Sheet } from '../Sheet';
import { draftFromImport, emptyDraft, importLink, siteOf, type RecipeDraft } from './draft';
import { preloadRecipeEditor } from './LazyRecipeEditor';

interface Props {
  onClose: () => void;
  /** Opens the editor with this draft: an import, or an empty one to start from scratch. */
  onDraft: (draft: RecipeDraft) => void;
}

type Source = 'link' | 'text' | 'photo';

interface ImportProblem {
  source: Source;
  message: string;
  /** The link couldn't give a recipe, so pasting the text or a screenshot is worth offering. */
  otherWays: boolean;
}

/** What the progress line says as an import goes on: reading a page, then one AI request. */
const PROGRESS: Record<Source, string[]> = {
  link: ['Reading the page…', 'Finding the recipe…', 'Matching ingredients to foods…', 'Adding up nutrition…'],
  text: ['Reading the recipe…', 'Matching ingredients to foods…', 'Adding up nutrition…'],
  photo: ['Reading your photo…', 'Matching ingredients to foods…', 'Adding up nutrition…'],
};
/** Without AI, a link import only reads the page. */
const LINK_PROGRESS_WITHOUT_AI = ['Reading the page…', 'Finding the recipe…', 'Matching ingredients by name…'];
/** When the progress line moves on (ms from the start): a few updates for screen readers, not a stream. */
const PROGRESS_AT = [2500, 6000, 11_000];
/** Recipe photos have small print: a little more detail than meal photos. */
const PHOTO_SIDE = 1600;

/** Link failures that aren't about the page (a bad link, another import running): other ways won't help. */
const NOT_THE_PAGE = new Set(['import_invalid_url', 'import_blocked_address', 'import_in_progress']);

/**
 * New recipe: from a link (first, since most recipes are one), pasted text, a photo or
 * screenshot, or from scratch. An import shows its progress, then hands a draft to the
 * editor. Nothing is saved until the person saves it there.
 */
export function ImportRecipeSheet({ onClose, onDraft }: Props) {
  // AI can run (aiAvailable); or it's on but waits for the reset, with today's requests used up or the
  // server-wide cap reached (allowance.exhausted: links still import, unmatched); or it's off (aiOff says why).
  const allowance = useAIAllowance();
  const aiAvailable = allowance.canUse;
  const offReason = useAIOffReason();
  const importRecipe = useImportRecipe();
  const client = useQueryClient();
  const baseId = useId();
  const ids = {
    link: `${baseId}-link`,
    linkHint: `${baseId}-link-hint`,
    text: `${baseId}-text`,
    textHint: `${baseId}-text-hint`,
    ways: `${baseId}-ways`,
    aiOff: `${baseId}-ai-off`,
    allowance: `${baseId}-allowance`,
    error: (source: Source) => `${baseId}-${source}-error`,
  };
  const [view, setView] = useState<'start' | 'text'>('start');
  const [link, setLink] = useState('');
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState<PreparedImage | null>(null);
  // The import running, if any.
  const [running, setRunning] = useState<{ source: Source; site: string | null } | null>(null);
  const [step, setStep] = useState(0);
  const [problem, setProblem] = useState<ImportProblem | null>(null);
  const controller = useRef<AbortController | null>(null);
  // Where focus goes back to once an import stops without a draft (an error, or Cancel).
  const focusAfter = useRef<Source | null>(null);
  const linkRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const pasteRef = useRef<HTMLButtonElement>(null);
  const photoButtonRef = useRef<HTMLButtonElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // The editor comes next: load it now, so it opens without a gap.
  useEffect(() => preloadRecipeEditor(), []);
  // Closing the sheet stops an import that's still running.
  useEffect(() => () => controller.current?.abort(), []);
  useEffect(
    () => () => {
      if (photo) URL.revokeObjectURL(photo.previewUrl);
    },
    [photo],
  );

  useEffect(() => {
    if (!running) return;
    setStep(0);
    cancelRef.current?.focus();
    const timers = PROGRESS_AT.map((ms, i) => window.setTimeout(() => setStep(i + 1), ms));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [running]);

  // Back from progress without a draft: to the field (or button) the import started from.
  useEffect(() => {
    if (running || !focusAfter.current) return;
    const from = focusAfter.current;
    focusAfter.current = null;
    (from === 'link' ? linkRef.current : from === 'text' ? textRef.current : photoButtonRef.current)?.focus();
  }, [running]);

  /** Shows progress for a new import, or returns null while one is already running. */
  const start = (source: Source, site: string | null = null): AbortController | null => {
    if (controller.current) return null;
    const ac = new AbortController();
    controller.current = ac;
    setProblem(null);
    setRunning({ source, site });
    return ac;
  };

  /**
   * Back from progress without a draft (an error, or Cancel), to where the import started.
   * Only the import that's showing can do this: a cancelled one finishing late changes nothing.
   */
  const stop = (ac: AbortController, source: Source, problem: ImportProblem | null) => {
    if (controller.current !== ac) return;
    controller.current = null;
    focusAfter.current = source;
    setProblem(problem);
    setRunning(null);
  };

  const send = async (ac: AbortController, request: RecipeImportRequest, source: Source) => {
    let draft: RecipeDraft;
    try {
      const imported = await importRecipe.mutateAsync({ request, signal: ac.signal });
      // Each ingredient starts in its line's unit where the person's kitchen measures have it.
      draft = draftFromImport(imported, await kitchenUnitsNow(client));
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      // A gateway's own timeout page (not one of ours): say it plainly.
      const gateway = err instanceof ApiError && code === 'error' && err.status >= 502;
      return stop(ac, source, {
        source,
        message: gateway ? 'That took too long. Try again in a moment.' : err instanceof Error ? err.message : "Couldn't import that recipe. Try again.",
        otherWays: source === 'link' && code.startsWith('import_') && !NOT_THE_PAGE.has(code),
      });
    }
    // Closed or cancelled just as it arrived: don't open the editor after all.
    if (ac.signal.aborted || controller.current !== ac) return;
    controller.current = null;
    onDraft(draft);
  };

  const run = (request: RecipeImportRequest, source: Source, site: string | null = null) => {
    const ac = start(source, site);
    if (ac) void send(ac, request, source);
  };

  /** Back to the form right away, even while a photo is still being prepared (nothing listens for the abort then). */
  const cancel = () => {
    const ac = controller.current;
    if (!ac || !running) return;
    ac.abort();
    stop(ac, running.source, null);
  };

  const submitLink = () => {
    const href = importLink(link);
    if (!href) {
      setProblem({
        source: 'link',
        message: link.trim() ? "That doesn't look like a link. Copy the recipe page's address, or use Paste text for a recipe's text." : "Paste a recipe page's link first.",
        otherWays: false,
      });
      linkRef.current?.focus();
      return;
    }
    void run({ url: href }, 'link', siteOf(href));
  };

  const submitText = () => {
    // The button waits while AI can't run, and the note above it says until when. The count may be
    // stale (an admin raised the limit, say), so pressing it asks the server again.
    if (!aiAvailable) {
      refreshAIUsage(client);
      return;
    }
    const recipe = text.trim();
    if (!recipe) {
      setProblem({ source: 'text', message: 'Paste the recipe first: at least its ingredients.', otherWays: false });
      textRef.current?.focus();
      return;
    }
    void run({ text: recipe }, 'text');
  };

  const openText = () => {
    setProblem(null);
    setView('text');
    requestAnimationFrame(() => textRef.current?.focus());
  };

  const backToStart = () => {
    setProblem(null);
    setView('start');
    requestAnimationFrame(() => pasteRef.current?.focus());
  };

  // The problem shown stays while the file picker is open: if the person backs out, it's still there.
  const pickPhoto = () => {
    if (aiAvailable) photoInputRef.current?.click();
  };

  const onPhoto = async (file: File | undefined) => {
    if (!file) return;
    // Not the last photo (one that was just turned down, say) while this one is prepared.
    setPhoto(null);
    // Progress shows while the photo is shrunk too: a big photo takes a moment on a phone.
    const ac = start('photo');
    if (!ac) return;
    let prepared: PreparedImage;
    try {
      prepared = await prepareImage(file, PHOTO_SIDE);
    } catch (err) {
      return stop(ac, 'photo', { source: 'photo', message: err instanceof Error ? err.message : "Couldn't use that photo.", otherWays: false });
    }
    // Cancelled or closed meanwhile: let the preview go.
    if (ac.signal.aborted) return URL.revokeObjectURL(prepared.previewUrl);
    setPhoto(prepared);
    void send(ac, { image: { mimeType: prepared.mimeType, base64: prepared.base64 } }, 'photo');
  };

  const errorFor = (source: Source) =>
    problem?.source === source ? (
      <p id={ids.error(source)} className="field__error" role="alert">
        {problem.message}
      </p>
    ) : null;

  const progressLines = running ? (running.source === 'link' && !aiAvailable ? LINK_PROGRESS_WITHOUT_AI : PROGRESS[running.source]) : [];
  const progress = progressLines[Math.min(step, progressLines.length - 1)] ?? '';
  const linkHint = aiAvailable
    ? `Most recipe sites work. ${APP_NAME} reads the page and matches the ingredients to foods.`
    : allowance.exhausted
      ? `Most recipe sites work. ${allowance.state === 'busy' ? 'AI is paused for today' : "You've used today's AI requests"}, so ${APP_NAME} matches the ingredients by name, as best guesses for you to check.`
      : `Most recipe sites work. AI is off, so ${APP_NAME} matches the ingredients by name, as best guesses for you to check.`;
  // What the AI-only ways point at when they can't be used: the "come back at" note, or why AI is off.
  const whyNoAI = allowance.exhausted ? ids.allowance : ids.aiOff;

  return (
    <Sheet title="New recipe" onClose={onClose}>
      {/* Always on the page (empty when idle): screen readers announce changes to a live region, not one that appears already filled. */}
      <p className="sr-only" role="status">
        {running ? progress : ''}
      </p>
      {/* A photo or screenshot comes from the camera or the photo library (no capture attribute, so both are offered). */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          void onPhoto(e.target.files?.[0]);
          e.target.value = '';
        }}
      />

      {running ? (
        // Not aria-busy: screen readers would hold back the progress line until the import ends.
        <div className="stack" style={{ gap: 14 }}>
          {running.source === 'photo' && photo && <img className="import-photo" src={photo.previewUrl} alt="Your photo" />}
          <p className="thinking" aria-hidden="true" style={{ margin: 0 }}>
            <span className="thinking__orb" /> {progress}
          </p>
          <p className="small muted" style={{ margin: 0 }}>
            {running.site ? `From ${running.site}. ` : ''}This can take up to a minute. Nothing is saved until you check it and save.
          </p>
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton" style={{ height: 52, opacity: 1 - i * 0.25 }} />
          ))}
          <button ref={cancelRef} type="button" className="btn btn--ghost" style={{ alignSelf: 'flex-start' }} onClick={cancel}>
            Cancel
          </button>
        </div>
      ) : view === 'text' ? (
        <form
          className="stack"
          style={{ gap: 14 }}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            submitText();
          }}
        >
          <button type="button" className="btn btn--quiet btn--sm" style={{ alignSelf: 'flex-start' }} onClick={backToStart}>
            <ChevronLeft size={16} aria-hidden="true" /> Other ways
          </button>
          <div className="field">
            <label className="field__label" htmlFor={ids.text}>
              Paste the recipe
            </label>
            <textarea
              ref={textRef}
              id={ids.text}
              className="textarea"
              rows={9}
              maxLength={IMPORT_LIMITS.text}
              placeholder={'2 cups flour\n1 tsp salt\n…'}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                if (problem?.source === 'text') setProblem(null);
              }}
              aria-invalid={problem?.source === 'text' ? true : undefined}
              aria-describedby={[ids.textHint, problem?.source === 'text' ? ids.error('text') : ''].filter(Boolean).join(' ')}
            />
            <span id={ids.textHint} className="field__hint">
              The ingredients, one per line, and the steps if you like.
              {text.length > IMPORT_LIMITS.text * 0.9 ? ` ${(IMPORT_LIMITS.text - text.length).toLocaleString('en-US')} characters left.` : ''}
            </span>
            {errorFor('text')}
          </div>
          <AIRequestsLeft id={ids.allowance} />
          {/* aria-disabled, not disabled: it stays reachable, and the note it points at is read out. */}
          <button
            type="submit"
            className={aiAvailable ? 'btn btn--ai btn--lg btn--block' : 'btn btn--ai btn--lg btn--block is-waiting'}
            aria-disabled={aiAvailable ? undefined : true}
            aria-describedby={allowance.exhausted ? ids.allowance : undefined}
          >
            <Sparkles size={18} aria-hidden="true" /> Read the recipe
          </button>
          <p className="tiny faint center" style={{ margin: 0 }}>
            Uses one of today's AI requests.
          </p>
        </form>
      ) : (
        <div className="stack" style={{ gap: 22 }}>
          <AIRequestsLeft id={ids.allowance} fallback="Links still import, with the ingredients matched by name for you to check." />
          <form
            className="stack"
            style={{ gap: 8 }}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              submitLink();
            }}
          >
            <label className="field__label" htmlFor={ids.link}>
              From a link
            </label>
            <div className="row">
              <input
                ref={linkRef}
                id={ids.link}
                className="input grow"
                type="text"
                inputMode="url"
                enterKeyHint="go"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="off"
                spellCheck={false}
                maxLength={IMPORT_LIMITS.url * 2}
                placeholder="https://…"
                value={link}
                onChange={(e) => {
                  setLink(e.target.value);
                  if (problem?.source === 'link') setProblem(null);
                }}
                aria-invalid={problem?.source === 'link' ? true : undefined}
                aria-describedby={[ids.linkHint, problem?.source === 'link' ? ids.error('link') : ''].filter(Boolean).join(' ')}
              />
              <button type="submit" className="btn btn--primary">
                Import
              </button>
            </div>
            <span id={ids.linkHint} className="field__hint">
              {linkHint}
            </span>
            {errorFor('link')}
            {problem?.source === 'link' && problem.otherWays && aiAvailable && (
              <div className="row wrap">
                <button type="button" className="chip" onClick={openText}>
                  <ClipboardPaste size={15} aria-hidden="true" /> Paste the text instead
                </button>
                <button type="button" className="chip" onClick={pickPhoto}>
                  <Camera size={15} aria-hidden="true" /> Use a screenshot
                </button>
              </div>
            )}
          </form>

          <div className="stack" style={{ gap: 6 }}>
            <h3 id={ids.ways} className="ngroup__title" style={{ margin: 0 }}>
              Other ways
            </h3>
            <div className="list" role="group" aria-labelledby={ids.ways}>
              {/* aria-disabled, not disabled: still reachable by keyboard, so the reason below is read out. */}
              <button
                ref={pasteRef}
                type="button"
                className="list-item import-way"
                aria-disabled={aiAvailable ? undefined : true}
                aria-describedby={aiAvailable ? undefined : whyNoAI}
                onClick={() => aiAvailable && openText()}
              >
                <span className="import-way__icon" aria-hidden="true">
                  <ClipboardPaste size={18} />
                </span>
                <span className="grow">
                  <span className="import-way__title">Paste text</span>
                  <span className="import-way__sub">From a note, an email or a message</span>
                </span>
                <ChevronRight size={18} className="muted" aria-hidden="true" style={{ flex: 'none' }} />
              </button>
              <button
                ref={photoButtonRef}
                type="button"
                className="list-item import-way"
                aria-disabled={aiAvailable ? undefined : true}
                aria-describedby={[aiAvailable ? '' : whyNoAI, problem?.source === 'photo' ? ids.error('photo') : ''].filter(Boolean).join(' ') || undefined}
                onClick={pickPhoto}
              >
                <span className="import-way__icon" aria-hidden="true">
                  <Camera size={18} />
                </span>
                <span className="grow">
                  <span className="import-way__title">Photo or screenshot</span>
                  <span className="import-way__sub">A cookbook page, a recipe card or a screenshot</span>
                </span>
                <ChevronRight size={18} className="muted" aria-hidden="true" style={{ flex: 'none' }} />
              </button>
              <button type="button" className="list-item import-way" onClick={() => onDraft(emptyDraft())}>
                <span className="import-way__icon" aria-hidden="true">
                  <PencilLine size={18} />
                </span>
                <span className="grow">
                  <span className="import-way__title">Start from scratch</span>
                  <span className="import-way__sub">Pick each ingredient yourself</span>
                </span>
                <ChevronRight size={18} className="muted" aria-hidden="true" style={{ flex: 'none' }} />
              </button>
            </div>
            {errorFor('photo')}
            {/* Waiting for the reset is explained by the note at the top; this is for AI being off. */}
            {!allowance.enabled && (
              <p id={ids.aiOff} className="tiny muted" style={{ margin: 0 }}>
                {/* Off until they agree: the prompt at the top says why and lets them agree, so this only points there. */}
                Pasted text and photos need AI. {offReason === 'consent' ? 'You can turn it on above.' : AI_OFF_FIX[offReason]}
              </p>
            )}
          </div>

          <p className="tiny faint center" style={{ margin: 0 }}>
            Nothing is saved until you check the recipe and save it.
          </p>
        </div>
      )}
    </Sheet>
  );
}
